import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Heart, MessageSquare, Car, Settings as SettingsIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CarCard from "../components/CarCard";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings");
  const [myListings, setMyListings] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [rentalReqs, setRentalReqs] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/account");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/listings", { params: { user_id: user.id, status: "" } }).then(({ data }) => setMyListings(data.items || []));
    api.get("/saved").then(({ data }) => setSavedItems(data || []));
    api.get("/enquiries").then(({ data }) => setEnquiries(data || []));
    api.get("/rental-requests").then(({ data }) => setRentalReqs(data || []));
  }, [user, tab]);

  if (!user) return null;

  const tabs = [
    { id: "listings", label: "My Listings", icon: Car, count: myListings.length },
    { id: "saved", label: "Saved", icon: Heart, count: savedItems.length },
    { id: "enquiries", label: "Enquiries", icon: MessageSquare, count: enquiries.length + rentalReqs.length },
    { id: "profile", label: "Profile", icon: SettingsIcon },
  ];

  return (
    <div data-testid="account-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-7 flex items-center gap-4">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-cm-primary text-white flex items-center justify-center font-display font-extrabold text-xl">
            {user.full_name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-extrabold text-xl md:text-2xl text-cm-primary truncate">{user.full_name}</h1>
            <p className="text-sm text-slate-500">{user.email} · <span className="capitalize">{user.role}</span></p>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium flex items-center gap-2">
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`account-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-cm-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
              <t.icon className="w-4 h-4" /> {t.label} {t.count != null && <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-xs">{t.count}</span>}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "listings" && (
            <div data-testid="account-listings">
              {myListings.length === 0 ? (
                <Empty cta={<Link to="/sell" data-testid="empty-sell-cta" className="px-5 py-3 rounded-xl bg-cm-primary text-white font-medium">List your first car</Link>}>You haven't listed any cars yet.</Empty>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {myListings.map((l) => (
                    <div key={l.id} className="relative">
                      <CarCard listing={l} />
                      <span className={`absolute top-3 left-3 z-10 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${l.status === 'approved' ? 'bg-emerald-500 text-white' : l.status === 'pending' ? 'bg-amber-500 text-white' : l.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-slate-500 text-white'}`}>{l.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "saved" && (
            <div data-testid="account-saved">
              {savedItems.length === 0 ? (
                <Empty>No saved cars yet. Tap the heart on any listing to save it.</Empty>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {savedItems.map((l) => <CarCard key={l.id} listing={l} />)}
                </div>
              )}
            </div>
          )}

          {tab === "enquiries" && (
            <div data-testid="account-enquiries" className="space-y-3">
              {enquiries.length === 0 && rentalReqs.length === 0 ? (
                <Empty>No enquiries received yet.</Empty>
              ) : (
                <>
                  {enquiries.map((e) => (
                    <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-cm-primary">{e.user_name} <span className="text-xs text-slate-500">· {e.phone}</span></div>
                          <p className="text-sm text-slate-600 mt-1">{e.message}</p>
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                  {rentalReqs.map((r) => (
                    <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-cm-primary">Rental: {r.customer_name} <span className="text-xs text-slate-500">· {r.phone}</span></div>
                          <p className="text-sm text-slate-600 mt-1">{r.pickup_date} → {r.return_date} · {r.pickup_location}</p>
                          {r.notes && <p className="text-xs text-slate-500 mt-1">{r.notes}</p>}
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {tab === "profile" && (
            <div data-testid="account-profile" className="bg-white border border-slate-200 rounded-2xl p-5 max-w-xl">
              <h3 className="font-display font-bold text-lg text-cm-primary">Profile</h3>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Item k="Full name" v={user.full_name} />
                <Item k="Email" v={user.email} />
                <Item k="Phone" v={user.phone || "—"} />
                <Item k="Role" v={user.role} />
                <Item k="Status" v={user.status} />
              </dl>
              {user.role !== 'dealer' && (
                <Link to="/register" className="mt-5 inline-block px-4 py-2.5 rounded-xl bg-cm-accent text-white text-sm font-medium">Become a dealer</Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty({ children, cta }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
      <p className="text-slate-500">{children}</p>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}

function Item({ k, v }) {
  return (
    <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
      <span className="text-slate-500 text-xs uppercase tracking-wider">{k}</span>
      <span className="font-medium text-cm-primary capitalize">{v}</span>
    </div>
  );
}
